import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ExternalLink, Users, Search, ArrowRight } from "lucide-react";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { SectionHeading } from "../../components/shared/SectionHeading";
import { EmptyState } from "../../components/shared/EmptyState";
import { api } from "../../lib/api";

const ARTRES = [50.2967, 3.5389]; // dépôt Art Event — Artres (59269)

// Distance à vol d'oiseau depuis le dépôt (indicative) — mètres sous 1 km, km au-delà
const haversineM = (lat, lng) => {
  const rad = Math.PI / 180;
  const dLat = (lat - ARTRES[0]) * rad;
  const dLng = (lng - ARTRES[1]) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(ARTRES[0] * rad) * Math.cos(lat * rad) * Math.sin(dLng / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};
const distanceLabel = (lat, lng) => {
  const m = haversineM(lat, lng);
  return m < 1000 ? `à ${Math.max(50, Math.round(m / 50) * 50)} m du dépôt` : `à ${Math.round(m / 1000)} km du dépôt`;
};
const distanceShort = (lat, lng) => {
  const m = haversineM(lat, lng);
  return m < 1000 ? `À ${Math.max(50, Math.round(m / 50) * 50)} m` : `À ${Math.round(m / 1000)} km`;
};

// Épingle sur mesure aux couleurs Art Event (goutte SVG) — lieux en vert profond, dépôt en doré
const pinIcon = (variant, active = false) => {
  const fill = variant === "depot" ? "#D4AF37" : "#2C4C3B";
  const dot = variant === "depot" ? "#2C4C3B" : "#D4AF37";
  return L.divIcon({
    className: "ae-pin",
    html: `<svg class="${active ? "ae-pin-bounce" : ""}" viewBox="0 0 32 44" xmlns="http://www.w3.org/2000/svg" data-variant="${variant}">
      <path d="M16 1C8.3 1 2 7.3 2 15c0 10.5 14 28 14 28s14-17.5 14-28C30 7.3 23.7 1 16 1z" fill="${fill}"/>
      <circle cx="16" cy="15" r="5.5" fill="${dot}"/>
    </svg>`,
    iconSize: [34, 46], iconAnchor: [17, 46], popupAnchor: [0, -42],
  });
};

export default function LieuxPage() {
  const [venues, setVenues] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("");
  const mapRef = useRef(null);
  useEffect(() => {
    api.get("/venues").then((r) => setVenues(r.data)).catch(() => setVenues([]));
  }, []);

  const allTags = [...new Set((venues || []).flatMap((v) => v.tags || []))];
  const filtered = (venues || []).filter((v) =>
    (!query || `${v.name} ${v.city}`.toLowerCase().includes(query.toLowerCase())) &&
    (!tag || (v.tags || []).includes(tag))
  );
  const mapped = filtered.filter((v) => v.lat && v.lng);

  const focusVenue = (v) => {
    if (mapRef.current && v.lat && v.lng) mapRef.current.flyTo([v.lat, v.lng], 13, { duration: 0.8 });
  };

  return (
    <div className="container-ae py-12 md:py-16" data-testid="lieux-page">
      <SectionHeading overline="Lieux de réception" title="Domaines & lieux d'exception" accent="où nous sommes déjà intervenus">
        Découvrez une sélection des magnifiques domaines du Nord et de la région où notre équipe a déjà eu le plaisir de concevoir et d'installer des scénographies sur mesure.
      </SectionHeading>

      {!venues ? (
        <p className="mt-10 text-center font-heading text-deepgreen" data-testid="lieux-loading">Chargement…</p>
      ) : venues.length === 0 ? (
        <div className="mt-10"><EmptyState title="Bientôt disponible">Nos lieux partenaires seront présentés ici très prochainement.</EmptyState></div>
      ) : (
        <>
          {/* Recherche + filtre par style de salle */}
          <div className="mt-10 flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-52">
              <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input className="input-ae !pl-10" placeholder="Rechercher par lieu…" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="lieux-search" />
            </div>
            {allTags.length > 0 && (
              <select className="input-ae w-auto" value={tag} onChange={(e) => setTag(e.target.value)} data-testid="lieux-tag-filter" aria-label="Filtrer par style de salle">
                <option value="">Filtrer : {allTags.join(" | ")}</option>
                {allTags.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
          </div>

          <div className="mt-6 grid items-start gap-8 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
            {/* Colonne gauche — liste des lieux */}
            <div className="order-2 lg:order-1">
              {filtered.length === 0 ? (
                <EmptyState title="Aucun lieu ne correspond">Essayez un autre nom ou un autre style de salle.</EmptyState>
              ) : (
                <div className="ae-scroll space-y-6 lg:max-h-[720px] lg:overflow-y-auto lg:pr-3" data-testid="lieux-list">
                  {filtered.map((v) => (
                    <article
                      key={v.id}
                      className={`card-ae cursor-pointer overflow-hidden transition-all duration-300 ${hovered === v.id ? "-translate-y-1 shadow-xl ring-1 ring-sage" : "hover:-translate-y-1 hover:shadow-lg"}`}
                      data-testid={`venue-card-${v.id}`}
                      onMouseEnter={() => { setHovered(v.id); }}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => focusVenue(v)}
                    >
                      {v.image ? (
                        <div className="relative">
                          <img src={v.image} alt={v.name} loading="lazy" className="aspect-[16/9] w-full object-cover" />
                          {(v.tags || []).length > 0 && (
                            <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
                              {v.tags.map((t) => (
                                <span key={t} className="rounded-full bg-cream/90 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-deepgreen backdrop-blur-sm" data-testid={`venue-tag-${t.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-")}`}>
                                  {t}
                                </span>
                              ))}
                            </div>
                          )}
                          {v.lat && v.lng && (
                            <span className="absolute bottom-3 left-3 rounded-full bg-deepgreen/85 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-cream backdrop-blur-sm" data-testid={`venue-distance-${v.id}`}>
                              {distanceShort(v.lat, v.lng)}
                            </span>
                          )}
                        </div>
                      ) : (
                        (v.tags || []).length > 0 && (
                          <div className="flex flex-wrap gap-1.5 px-5 pt-5">
                            {v.tags.map((t) => (
                              <span key={t} className="rounded-full bg-sage-light px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-deepgreen" data-testid={`venue-tag-${t.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-")}`}>
                                {t}
                              </span>
                            ))}
                          </div>
                        )
                      )}
                      <div className="p-5">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h3 className="font-heading text-xl text-deepgreen">{v.name}</h3>
                          {v.capacity && <span className="inline-flex items-center gap-1 rounded-full bg-sage-light px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-deepgreen"><Users className="h-3 w-3" /> {v.capacity}</span>}
                        </div>
                        <p className="mt-1 flex items-center gap-1.5 text-sm text-muted-foreground">
                          <MapPin className="h-4 w-4 text-sage" /> {v.city}{v.address ? ` · ${v.address}` : ""}
                        </p>
                        {v.description && <p className="mt-2.5 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{v.description}</p>}
                        {(v.realisations || []).length > 0 && (
                          <Link
                            to={`/realisations/${v.realisations[0].slug}`}
                            onClick={(e) => e.stopPropagation()}
                            className="btn-secondary-ae mt-4 w-full !py-2.5 !text-[11px]"
                            data-testid={`venue-realisations-cta-${v.id}`}
                          >
                            Voir les réalisations dans ce lieu <ArrowRight className="h-3.5 w-3.5" />
                          </Link>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {(v.realisations || []).slice(1).map((r) => (
                            <Link key={r.slug} to={`/realisations/${r.slug}`} onClick={(e) => e.stopPropagation()} className="rounded-full bg-cream px-3 py-1 text-[11px] font-semibold text-deepgreen transition-colors hover:bg-sage-light" data-testid={`venue-realisation-${r.slug}`}>
                              {r.title}
                            </Link>
                          ))}
                          {(v.products || []).map((p) => (
                            <Link key={p.slug} to={`/produit/${p.slug}`} onClick={(e) => e.stopPropagation()} className="rounded-full border border-sage px-3 py-1 text-[11px] font-semibold text-deepgreen transition-colors hover:bg-sage-light" data-testid={`venue-product-${p.slug}`}>
                              {p.name}
                            </Link>
                          ))}
                          {v.website && (
                            <a href={v.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-deepgreen hover:text-sage" data-testid={`venue-website-${v.id}`}>
                              Site du lieu <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>

            {/* Colonne droite — carte interactive fixe */}
            <div className="order-1 lg:order-2 lg:sticky lg:top-24">
              {mapped.length > 0 && (
                <div className="overflow-hidden rounded-2xl border border-border shadow-lg" data-testid="lieux-map">
                  <MapContainer
                    ref={mapRef}
                    bounds={L.latLngBounds([ARTRES, ...mapped.map((v) => [v.lat, v.lng])]).pad(0.25)}
                    boundsOptions={{ maxZoom: 12 }}
                    scrollWheelZoom={false}
                    style={{ height: 340, width: "100%" }}
                    className="lg:!h-[620px]"
                  >
                    {/* Tuiles OpenStreetMap adoucies (fond beige/épuré style Positron) via filtre CSS */}
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={ARTRES} icon={pinIcon("depot")}>
                      <Popup>
                        <div className="ae-popup" data-testid="venue-popup-depot">
                          <div>
                            <p className="ae-popup-name">Art Event — Dépôt</p>
                            <p className="ae-popup-city">Artres (59269) · retrait gratuit sur RDV</p>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                    {mapped.map((v) => (
                      <Marker key={v.id} position={[v.lat, v.lng]} icon={pinIcon("venue", hovered === v.id)} zIndexOffset={500}>
                        <Popup>
                          <div className="ae-popup" data-testid={`venue-popup-${v.id}`}>
                            {v.image && <img src={v.image} alt={v.name} className="ae-popup-img" />}
                            <div>
                              <p className="ae-popup-name">{v.name}</p>
                              <p className="ae-popup-city">{v.city} · {distanceLabel(v.lat, v.lng)}</p>
                              {(v.realisations || []).length > 0 && (
                                <Link to={`/realisations/${v.realisations[0].slug}`} className="ae-popup-link" data-testid={`venue-popup-cta-${v.id}`}>
                                  Voir le lieu →
                                </Link>
                              )}
                            </div>
                          </div>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
